import React from 'react';
import LegalPageLayout, { Section, Paragraph } from '../../components/LegalPageLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Mail, MessageSquare, Clock } from 'lucide-react';

const Contact = () => {
    return (
        <LegalPageLayout title="Contact / Support" lastUpdated={null}>
            <Section title="Get in Touch">
                <Paragraph>
                    We're here to help. Whether you have questions about using ClientCheck, 
                    need to report a policy violation, or have feedback about the platform, 
                    our support team is available to assist you.
                </Paragraph>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                <Card className="bg-card border-border">
                    <CardContent className="p-6">
                        <Mail className="h-8 w-8 text-primary mb-4" />
                        <h3 className="font-semibold text-foreground mb-2">Email Support</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            For general inquiries, policy questions, or account issues.
                        </p>
                        <a 
                            href="mailto:support@clientcheck.com" 
                            className="text-primary hover:underline text-sm font-medium"
                        >
                            support@clientcheck.com
                        </a>
                    </CardContent>
                </Card>

                <Card className="bg-card border-border">
                    <CardContent className="p-6">
                        <Clock className="h-8 w-8 text-primary mb-4" />
                        <h3 className="font-semibold text-foreground mb-2">Response Time</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            We aim to respond to all inquiries within 1-2 business days.
                        </p>
                        <span className="text-sm text-muted-foreground">
                            Monday - Friday, 9am - 5pm EST
                        </span>
                    </CardContent>
                </Card>
            </div>

            <Section title="Common Support Topics">
                <div className="space-y-4">
                    <div className="p-4 bg-card/50 border border-border rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Account Issues</h4>
                        <p className="text-sm text-muted-foreground">
                            Login problems, password resets, account settings, or profile updates.
                        </p>
                    </div>
                    <div className="p-4 bg-card/50 border border-border rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Review Questions</h4>
                        <p className="text-sm text-muted-foreground">
                            Questions about submitting reviews, content guidelines, or review moderation.
                        </p>
                    </div>
                    <div className="p-4 bg-card/50 border border-border rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Policy Violations</h4>
                        <p className="text-sm text-muted-foreground">
                            Report content that violates our community standards or policies.
                        </p>
                    </div>
                    <div className="p-4 bg-card/50 border border-border rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Ownership Resets</h4>
                        <p className="text-sm text-muted-foreground">
                            Request a property ownership reset with appropriate documentation.
                        </p>
                    </div>
                    <div className="p-4 bg-card/50 border border-border rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Privacy Requests</h4>
                        <p className="text-sm text-muted-foreground">
                            Data access requests, deletion requests, or privacy concerns.
                        </p>
                    </div>
                </div>
            </Section>

            <Section title="Before You Contact Us">
                <Paragraph>
                    Many questions are answered in our policy documents:
                </Paragraph>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2 mt-2">
                    <li><a href="/terms" className="text-primary hover:underline">Terms of Service</a> — Platform rules and user responsibilities</li>
                    <li><a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> — How we handle your data</li>
                    <li><a href="/content-guidelines" className="text-primary hover:underline">Content Guidelines</a> — How to write effective reviews</li>
                    <li><a href="/acceptable-use" className="text-primary hover:underline">Acceptable Use Policy</a> — What's allowed and prohibited</li>
                    <li><a href="/ownership-reset-policy" className="text-primary hover:underline">Ownership Reset Policy</a> — How property resets work</li>
                </ul>
            </Section>

            <div className="mt-8 p-6 bg-card border border-border rounded-lg">
                <div className="flex items-start gap-4">
                    <MessageSquare className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="font-semibold text-foreground mb-2">Feedback Welcome</h3>
                        <p className="text-sm text-muted-foreground">
                            We're constantly working to improve ClientCheck. If you have suggestions 
                            for new features, improvements, or general feedback, we'd love to hear 
                            from you. Your input helps us build a better platform for the contractor community.
                        </p>
                    </div>
                </div>
            </div>
        </LegalPageLayout>
    );
};

export default Contact;
